/**
 * Authentication Configuration
 * Ported from: innovation-claude/packages/tradovate-client/src/authenticator/authentication_config.rs
 *
 * Provides URL generation for all Tradovate API endpoints based on:
 * - Stage (Devel, Staging, UAT, Production)
 * - Environment (Demo, Live)
 */

import type { TradovateStage, TradovateEnvironment } from './types'
import {
    buildAuthHttpUrl,
    buildPrimaryHttpUrl,
    buildPrimaryWsUrl,
    buildMdWsUrl,
    buildReportingHttpUrl,
    buildRemoteStorageHttpUrl,
    type UrlBuilderOptions,
} from './urlBuilder'

/**
 * Complete authentication configuration with URL generation
 * Ported from authentication_config.rs:36-126
 *
 * BREAKING CHANGE: opts.baseApiUrl is now REQUIRED to prevent accidental
 * connections to production environment.
 */
export class AuthenticationConfig {
    public readonly stage: TradovateStage
    public readonly environment: TradovateEnvironment
    public readonly appId: string
    public readonly appVersion: string
    private readonly opts: UrlBuilderOptions

    constructor(
        stage: TradovateStage,
        environment: TradovateEnvironment | undefined,
        appId: string = 'innolab-trader',
        appVersion: string = '0.1.0',
        opts: UrlBuilderOptions
    ) {
        this.stage = stage
        // Default unknown/omitted environment to Live
        this.environment = environment ?? 'Live'
        this.appId = appId
        this.appVersion = appVersion
        this.opts = opts
    }

    /**
     * Get authentication HTTP URL
     * Used for /auth/* endpoints (login, register, etc.)
     */
    authHttpUrl(): string {
        return buildAuthHttpUrl(this.stage, this.environment, this.opts)
    }

    /**
     * Get primary HTTP URL for general API calls
     * Used for /order/*, /account/*, etc.
     */
    primaryHttpUrl(): string {
        return buildPrimaryHttpUrl(this.stage, this.environment, this.opts)
    }

    /**
     * Get primary WebSocket URL for services
     * Used for order updates, account updates, etc.
     */
    primaryWsUrl(): string {
        return buildPrimaryWsUrl(this.stage, this.environment, this.opts)
    }

    /**
     * Get market data WebSocket URL
     * Used for quotes, DOM, histograms
     */
    mdWsUrl(): string {
        return buildMdWsUrl(this.stage, this.environment, this.opts)
    }

    /**
     * Get reporting HTTP URL
     * Used for reports, statements, etc.
     */
    reportingHttpUrl(): string {
        return buildReportingHttpUrl(this.stage, this.environment, this.opts)
    }

    /**
     * Get remote storage HTTP URL
     * Used for workspace layouts, user preferences
     */
    remoteStorageHttpUrl(): string {
        return buildRemoteStorageHttpUrl(this.stage, this.environment, this.opts)
    }

    /**
     * Check if this is a demo environment
     */
    isDemo(): boolean {
        return this.environment === 'Demo'
    }

    /**
     * Get the current stage and environment
     */
    getStageEnvironment(): [TradovateStage, TradovateEnvironment] {
        return [this.stage, this.environment]
    }
}
